"use client"

/**
 * CRM Settings Tab for Messaging Agents
 *
 * Simplified version of email CRM settings:
 * - CRM integration selection (uses crm_bindings with scopeType: "message_agent")
 * - Connection status
 * - Opportunity routing: per-channel stages, AI-excluded contacts, human escalation
 *   (each destination has its own on/off switch)
 * - NO email sorting / category mapping (not applicable for messaging)
 * - NO create_note_on_reply (message agent uses session close notes, not per-reply notes)
 */

import { useEffect, useState, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Link2,
  AlertCircle,
  AlertTriangle,
  UserX,
  CheckCircle2,
  XCircle,
  Unplug,
  Plus,
  Loader2,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useCrmBinding, type CrmStage } from "@/hooks/integrations/use-crm-binding"
import { useCrmIntegrations } from "@/hooks/integrations/use-crm-integrations"
import type { CrmProviderType, MessagingLeadPolicy } from "@/lib/crm/types"
import { AddCrmIntegrationDialog, CrmProviderLogo } from "@/components/crm"
import { HandoverPipelineSelector } from "@/components/agents/email/handover-pipeline-selector"
import { PageLoader } from "@/components/ui/page-loader"
import { usePermissions } from "@/hooks/use-permissions"
import { CRM_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useAuth } from "@/hooks/auth/use-auth"
import type { ChannelType, MessageAgentConfig } from "@/lib/agents/messaging/types"
import { CHANNEL_LABELS } from "@/lib/agents/messaging/types"

interface CrmSettingsProps {
  agentId: string
  canManage: boolean
  channels: Array<{ id: string; channel_type: string; account_name: string | null; status: string }>
}

export function CrmSettings({ agentId, canManage, channels }: CrmSettingsProps) {
  const { fetchWithAuth } = useAuth()
  const { can } = usePermissions()

  const {
    binding,
    integrations,
    pipelines,
    selectedIntegration,
    isLoading,
    isLoadingPipelines,
    pipelinesError,
    isSaving,
    error,
    loadIntegrations,
    saveBinding,
    updateBindingConfig,
    getStagesForPipeline,
    hasTenantDefault,
    tenantDefaultIntegration,
  } = useCrmBinding({
    scopeType: "message_agent",
    scopeId: agentId,
    autoLoad: true,
  })

  const {
    providers,
    isConnecting,
    connectIntegration,
    prepareOAuthInstall,
    error: crmError,
    clearError: clearCrmError,
    refreshIntegrations: refreshCrmIntegrations,
  } = useCrmIntegrations({ autoLoad: true })

  // Agent config for handover_crm
  const [agentConfig, setAgentConfig] = useState<MessageAgentConfig>({})
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)

  const [handoverStages, setHandoverStages] = useState<CrmStage[]>([])
  const [connectionTestResult, setConnectionTestResult] = useState<{
    success: boolean
    message: string
  } | null>(null)
  const [showAddCrmDialog, setShowAddCrmDialog] = useState(false)

  // Load agent config
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchWithAuth(`/api/agents/messaging/${agentId}`)
        if (res.ok) {
          const data = await res.json()
          setAgentConfig(data.agent?.config ?? {})
        }
      } catch {
        // ignore — CRM tab still works without agent config
      } finally {
        setIsLoadingConfig(false)
      }
    }
    load()
    loadIntegrations()
  }, [fetchWithAuth, agentId, loadIntegrations])

  // Load handover stages
  useEffect(() => {
    if (binding?.config?.handover?.pipeline_id) {
      setHandoverStages(getStagesForPipeline(binding.config.handover.pipeline_id))
    } else {
      setHandoverStages([])
    }
  }, [binding?.config?.handover?.pipeline_id, pipelines, getStagesForPipeline])

  const isCrmConnected = !!binding
  const isCrmEnabled = isCrmConnected && binding.config?.enabled !== false
  const isSentimentEnabled = agentConfig.tools_enabled?.assess_sentiment === true
  // `chatAvailable` = adapter AND this environment's channel credentials. The provider
  // name alone would show the toggle, and auto-write the ON below, where pushes throw.
  const providerHasChat =
    !!selectedIntegration &&
    providers.find((p) => p.id === selectedIntegration.provider)?.chatAvailable === true
  const replyViaCrm = binding?.config?.messaging?.reply_via_crm
  const isReplyViaCrmEnabled = replyViaCrm !== false // undefined → treat as ON for the UI

  // First time the user lands on the settings page for an agent whose CRM integration supports
  // chat AND the binding has no explicit reply_via_crm value yet — write the implicit ON to the
  // SQL row so the server-side opt-in check (`messaging.reply_via_crm === true`) starts mirroring
  // immediately. Subsequent visits do nothing (`replyViaCrm` is set, so the condition is false).
  // Guard on canManage so viewers don't auto-mutate state.
  useEffect(() => {
    if (
      canManage &&
      isCrmEnabled &&
      providerHasChat &&
      replyViaCrm === undefined &&
      !isSaving
    ) {
      void updateBindingConfig({
        messaging: { ...binding?.config?.messaging, reply_via_crm: true },
      })
    }
  }, [
    canManage,
    isCrmEnabled,
    providerHasChat,
    replyViaCrm,
    isSaving,
    binding?.config?.messaging,
    updateBindingConfig,
  ])

  const handleIntegrationChange = async (integrationId: string) => {
    setConnectionTestResult(null)
    if (integrationId === "none") {
      if (binding) {
        // Disable without clearing pipeline config — user can re-enable later
        await updateBindingConfig({ enabled: false, explicitly_disabled: true })
      } else if (integrations.length > 0) {
        // Create binding to override tenant default with explicit opt-out
        await saveBinding(integrations[0].id, { enabled: false, explicitly_disabled: true })
      }
      return
    }
    // Switching to a different integration resets pipeline config;
    // re-enabling the same integration preserves it
    if (binding?.integrationId === integrationId) {
      await updateBindingConfig({ enabled: true, explicitly_disabled: false })
    } else {
      await saveBinding(integrationId, { enabled: true })
    }
  }

  const handleEnabledToggle = async (enabled: boolean) => {
    await updateBindingConfig({ enabled })
  }

  const handleReplyViaCrmToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      messaging: { ...binding?.config?.messaging, reply_via_crm: enabled },
    })
  }

  const handleReuseOpenOpportunityToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      messaging: { ...binding?.config?.messaging, reuse_open_opportunity: enabled },
    })
  }

  const handleLeadPolicyChange = async (policy: MessagingLeadPolicy) => {
    await updateBindingConfig({
      messaging: { ...binding?.config?.messaging, lead_policy: policy },
    })
  }

  const handleSentimentToggle = async (requireSentiment: boolean) => {
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        require_sentiment: requireSentiment,
      },
    })
  }

  const handleChannelPipelineChange = async (channelType: string, pipelineId: string) => {
    const prev = binding?.config?.messaging?.channels ?? {}
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        channels: { ...prev, [channelType]: { ...prev[channelType], pipeline_id: pipelineId, stage_id: undefined } },
      },
    })
  }

  const handleChannelStageChange = async (channelType: string, stageId: string) => {
    const prev = binding?.config?.messaging?.channels ?? {}
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        channels: { ...prev, [channelType]: { ...prev[channelType], stage_id: stageId } },
      },
    })
  }

  const handleLeadsWhileAiOffToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      messaging: { ...binding?.config?.messaging, leads_while_ai_off: enabled },
    })
  }

  const handleExclusionEnabledToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        exclusion: { ...binding?.config?.messaging?.exclusion, enabled },
      },
    })
  }

  const handleExclusionPipelineChange = async (pipelineId: string) => {
    const prevEnabled = binding?.config?.messaging?.exclusion?.enabled
    if (pipelineId === "channel_default") {
      // Unset: shallow top-level merge replaces `messaging` wholesale, and
      // JSON.stringify drops the undefined key — the stage override is removed
      // while an explicit on/off choice survives.
      await updateBindingConfig({
        messaging: {
          ...binding?.config?.messaging,
          exclusion: prevEnabled === undefined ? undefined : { enabled: prevEnabled },
        },
      })
      return
    }
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        exclusion: { enabled: prevEnabled, pipeline_id: pipelineId, stage_id: undefined },
      },
    })
  }

  const handleExclusionStageChange = async (stageId: string) => {
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        exclusion: { ...binding?.config?.messaging?.exclusion, stage_id: stageId },
      },
    })
  }

  const handleHandoverEnabledToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      handover: { ...binding?.config?.handover, enabled },
    })
  }

  const handleHandoverPipelineChange = async (pipelineId: string) => {
    await updateBindingConfig({
      handover: { ...binding?.config?.handover, pipeline_id: pipelineId, stage_id: undefined },
    })
  }

  const handleHandoverStatusChange = async (statusId: string) => {
    await updateBindingConfig({
      handover: { ...binding?.config?.handover, stage_id: statusId },
    })
  }

  const handleConnectCrm = useCallback(
    async (provider: string, name: string, credentials: Record<string, unknown>, metadata?: Record<string, unknown>) => {
      const result = await connectIntegration(provider as CrmProviderType, name, credentials, metadata)
      if (result.success) {
        await loadIntegrations()
        await refreshCrmIntegrations()
      }
      return result
    },
    [connectIntegration, loadIntegrations, refreshCrmIntegrations]
  )

  const getProviderInfo = useCallback(
    (providerId: string) => providers.find((p) => p.id === providerId),
    [providers]
  )

  const getProviderDisplayName = (provider: string) => {
    const names: Record<string, string> = { kommo: "Kommo", hubspot: "HubSpot", salesforce: "Salesforce", pipedrive: "Pipedrive" }
    return names[provider] || provider
  }

  // Reuse must never write into a finished deal, so it is only offered for
  // providers whose adapter can report closed state.
  const providerReportsClosedState = selectedIntegration
    ? getProviderInfo(selectedIntegration.provider)?.capabilities?.reportsClosedState === true
    : false

  const terminology = selectedIntegration ? getProviderInfo(selectedIntegration.provider)?.terminology : null
  const t = terminology || { opportunity: "opportunity", opportunityPlural: "opportunities", pipeline: "pipeline", stage: "stage" }
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

  const bindingConfig = binding?.config || {}
  const channelConfigs = bindingConfig.messaging?.channels ?? {}
  const connectedChannels = channels.filter((c) => c.status === "connected")

  const leadPolicy: MessagingLeadPolicy = bindingConfig.messaging?.lead_policy ?? "pii_required"

  // Both CRM destinations default to on — absent config means "as before".
  const exclusionConfig = bindingConfig.messaging?.exclusion ?? {}
  const isExclusionCrmEnabled = exclusionConfig.enabled !== false
  const isHandoverCrmEnabled = bindingConfig.handover?.enabled !== false
  const exclusionStages = exclusionConfig.pipeline_id
    ? getStagesForPipeline(exclusionConfig.pipeline_id).filter((s) => !s.isArchived)
    : []

  if (isLoading || isLoadingConfig) {
    return <PageLoader message="Loading CRM settings..." />
  }

  if (!can(CRM_PERMISSIONS.VIEW)) {
    return (
      <Card>
        <CardHeader><CardTitle>Access Denied</CardTitle></CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>You don&apos;t have permission to access CRM settings.</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
      )}

      {/* CRM Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            <span>CRM Integration</span>
          </CardTitle>
          <CardDescription>
            Connect this agent to your CRM to create leads from conversations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {integrations.length === 0 ? (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-yellow-800">
                    No CRM integrations available. Connect a CRM to sync contacts and conversations.
                  </span>
                </div>
              </div>
              {canManage && (
                <Button variant="outline" onClick={() => setShowAddCrmDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  <span>Connect CRM</span>
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Integration selector */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">CRM Connection</Label>
                <Select
                  value={binding?.config?.explicitly_disabled ? "none" : (binding?.integrationId || "")}
                  onValueChange={handleIntegrationChange}
                  disabled={isSaving || !canManage}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a CRM integration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2">
                        <Unplug className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">None / Disabled</span>
                      </div>
                    </SelectItem>
                    {integrations.map((integration) => {
                      const providerInfo = getProviderInfo(integration.provider)
                      return (
                        <SelectItem key={integration.id} value={integration.id}>
                          <div className="flex items-center gap-2">
                            {providerInfo?.logoUrl ? (
                              <CrmProviderLogo provider={integration.provider} logoUrl={providerInfo.logoUrl} size={16} />
                            ) : (
                              <div className={cn("w-4 h-4 rounded-full", integration.isActive ? "bg-green-500" : "bg-gray-300")} />
                            )}
                            <span>
                              {integration.name}{" "}
                              <span className="text-gray-400">({getProviderDisplayName(integration.provider)})</span>
                            </span>
                            {!integration.isActive && (
                              <Badge variant="secondary" className="text-xs ml-1">Disabled</Badge>
                            )}
                          </div>
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Enable/disable lead creation toggle (visible when integration selected) */}
              {isCrmConnected && !binding?.config?.explicitly_disabled && (
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    id="crm-enabled"
                    checked={isCrmEnabled}
                    onCheckedChange={handleEnabledToggle}
                    disabled={isSaving || !canManage}
                  />
                  <Label htmlFor="crm-enabled" className="text-sm cursor-pointer">
                    <span>Create</span> {t.opportunityPlural.toLowerCase()} <span>from conversations</span>
                  </Label>
                </div>
              )}

              {/* Mirror conversations into the CRM chat thread (per-agent, chat-capable providers only) */}
              {isCrmEnabled && providerHasChat && (
                <div className="flex items-start gap-3 p-3 border rounded-lg">
                  <Switch
                    id="crm-reply-via-chat"
                    checked={isReplyViaCrmEnabled}
                    onCheckedChange={handleReplyViaCrmToggle}
                    disabled={isSaving || !canManage}
                    className="mt-0.5"
                  />
                  <Label htmlFor="crm-reply-via-chat" className="text-sm cursor-pointer flex-1">
                    <div>Mirror conversations to CRM chat</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Send customer, AI and operator messages into the CRM lead&apos;s chat thread,
                      and let operators reply from inside the CRM.
                    </div>
                  </Label>
                </div>
              )}

              {/* Enabled where it cannot run: hiding the toggle would leave no way off. */}
              {isCrmEnabled && !providerHasChat && replyViaCrm === true && (
                <div className="flex items-start gap-3 p-3 border border-amber-300 bg-amber-50 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-amber-900">
                      CRM chat mirroring is enabled but unavailable
                    </div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      This agent is set to mirror conversations into the CRM chat, but the chat
                      channel is not configured for this environment. Nothing is being mirrored
                      and operator replies from the CRM will not arrive.
                    </div>
                    {canManage && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        disabled={isSaving}
                        onClick={() => handleReplyViaCrmToggle(false)}
                      >
                        Turn off mirroring
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {canManage && (
                <Button variant="outline" size="sm" onClick={() => setShowAddCrmDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  <span>Or add a different CRM</span>
                </Button>
              )}

              {isCrmEnabled && selectedIntegration && !pipelinesError && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800">
                    Connected to {selectedIntegration.name} ({getProviderDisplayName(selectedIntegration.provider)})
                  </span>
                </div>
              )}

              {pipelinesError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800">{pipelinesError}</span>
                </div>
              )}

              {!binding && hasTenantDefault && tenantDefaultIntegration && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    Using tenant default: {tenantDefaultIntegration.name} ({getProviderDisplayName(tenantDefaultIntegration.provider)}).
                    Select a specific integration to override.
                  </span>
                </div>
              )}

              {connectionTestResult && (
                <div className={cn(
                  "flex items-center gap-2 p-3 rounded-lg",
                  connectionTestResult.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
                )}>
                  {connectionTestResult.success
                    ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                    : <XCircle className="w-4 h-4 text-red-600" />}
                  <span className={cn("text-sm", connectionTestResult.success ? "text-green-800" : "text-red-800")}>
                    {connectionTestResult.message}
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Opportunity routing — channels, excluded contacts, human escalation */}
      {isCrmEnabled && selectedIntegration && (
        <Card>
          <CardHeader>
            <CardTitle>{capitalize(t.opportunity)} <span>Routing</span></CardTitle>
            <CardDescription>
              <span>Where</span> {t.opportunityPlural.toLowerCase()} <span>are created and moved.</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoadingPipelines ? (
              <div className="flex items-center gap-2 p-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm text-gray-500">Loading {t.pipeline}s...</span>
              </div>
            ) : (
              <>
                {connectedChannels.length > 0 && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Channels</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <span>Where new</span> {t.opportunityPlural.toLowerCase()} <span>are created for each channel.</span>
                      </p>
                    </div>

                    {connectedChannels.map((ch) => {
                      const chConf = channelConfigs[ch.channel_type] ?? {}
                      const chStages = chConf.pipeline_id
                        ? getStagesForPipeline(chConf.pipeline_id).filter((s) => !s.isArchived)
                        : []

                      return (
                        <div key={ch.id} className="space-y-3">
                          <Label className="text-sm font-normal text-muted-foreground">
                            {CHANNEL_LABELS[ch.channel_type as ChannelType] || ch.channel_type}
                            {ch.account_name && <span> — {ch.account_name}</span>}
                          </Label>
                          <div className="grid grid-cols-2 gap-4">
                            <Select
                              value={chConf.pipeline_id || ""}
                              onValueChange={(v) => handleChannelPipelineChange(ch.channel_type, v)}
                              disabled={isSaving || !canManage}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`${capitalize(t.pipeline)}...`} />
                              </SelectTrigger>
                              <SelectContent>
                                {pipelines.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name} <span>{p.isDefault && "(Default)"}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={chConf.stage_id || ""}
                              onValueChange={(v) => handleChannelStageChange(ch.channel_type, v)}
                              disabled={!chConf.pipeline_id || isSaving || !canManage}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`${capitalize(t.stage)}...`} />
                              </SelectTrigger>
                              <SelectContent>
                                {chStages.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>
                                    <div className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || "#9ca3af" }} />
                                      <span>{s.name}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )
                    })}

                    {connectedChannels.some((ch) => !channelConfigs[ch.channel_type]?.pipeline_id) && (
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-yellow-800">
                          Select {t.pipeline} and {t.stage} for each channel to create CRM {t.opportunityPlural.toLowerCase()}.
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* AI-excluded contacts: the one flow with no LLM turn, so its stage is config-only */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium">AI-excluded contacts</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {isExclusionCrmEnabled
                          ? `Contacts the AI never answers. Leave the ${t.stage.toLowerCase()} unset to use the channel ${t.stage.toLowerCase()}.`
                          : `Excluded contacts create no ${t.opportunityPlural.toLowerCase()}. Their conversations still pause and keep storing messages.`}
                      </p>
                    </div>
                    <Switch
                      checked={isExclusionCrmEnabled}
                      onCheckedChange={handleExclusionEnabledToggle}
                      disabled={isSaving || !canManage}
                    />
                  </div>
                  {isExclusionCrmEnabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <Select
                        value={exclusionConfig.pipeline_id || "channel_default"}
                        onValueChange={handleExclusionPipelineChange}
                        disabled={isSaving || !canManage}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={`${capitalize(t.pipeline)}...`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="channel_default">
                            <span className="text-gray-500">Use channel {t.stage.toLowerCase()} (default)</span>
                          </SelectItem>
                          {pipelines.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} <span>{p.isDefault && "(Default)"}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={exclusionConfig.stage_id || ""}
                        onValueChange={handleExclusionStageChange}
                        disabled={!exclusionConfig.pipeline_id || isSaving || !canManage}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={`${capitalize(t.stage)}...`} />
                        </SelectTrigger>
                        <SelectContent>
                          {exclusionStages.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || "#9ca3af" }} />
                                <span>{s.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Channels whose AI replies are switched off (channel toggle) */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium">Channels with AI replies off</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {bindingConfig.messaging?.leads_while_ai_off === true
                          ? `New conversations on such channels still create ${t.opportunityPlural.toLowerCase()}.`
                          : `No new ${t.opportunity.toLowerCase()} is created on such channels; an existing one keeps its notes.`}
                      </p>
                    </div>
                    <Switch
                      checked={bindingConfig.messaging?.leads_while_ai_off === true}
                      onCheckedChange={handleLeadsWhileAiOffToggle}
                      disabled={isSaving || !canManage}
                    />
                  </div>
                </div>

                {/* Human escalation — only meaningful when the tool can fire */}
                {agentConfig.tools_enabled?.human_handover === true && (
                  <div className="space-y-3 border-t pt-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <UserX className="w-4 h-4 text-red-500" />
                          <span>Human escalation</span>
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {isHandoverCrmEnabled
                            ? `Where the ${t.opportunity.toLowerCase()} goes when the AI escalates to a human — including escalations the customer is not told about.`
                            : `Escalations do not touch the CRM at all.`}
                        </p>
                      </div>
                      <Switch
                        checked={isHandoverCrmEnabled}
                        onCheckedChange={handleHandoverEnabledToggle}
                        disabled={isSaving || !canManage}
                      />
                    </div>
                    {isHandoverCrmEnabled && (
                      <HandoverPipelineSelector
                        layout="row"
                        pipelines={pipelines}
                        stages={handoverStages}
                        selectedPipelineId={bindingConfig.handover?.pipeline_id}
                        selectedStageId={bindingConfig.handover?.stage_id}
                        isLoadingPipelines={isLoadingPipelines}
                        isSaving={isSaving}
                        showWarning
                        onPipelineChange={handleHandoverPipelineChange}
                        onStageChange={handleHandoverStatusChange}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Session lead policy — every condition that gates an ordinary conversation's lead */}
      {isCrmEnabled && selectedIntegration && (
        <Card>
          <CardHeader>
            <CardTitle><span>Session</span> {t.opportunity.toLowerCase()} <span>policy</span></CardTitle>
            <CardDescription>
              <span>Choose when an ordinary conversation creates a</span> {t.opportunity.toLowerCase()}<span>.</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Select
              value={leadPolicy}
              onValueChange={(v) => handleLeadPolicyChange(v as MessagingLeadPolicy)}
              disabled={isSaving || !canManage}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pii_required">When contact info is captured (default)</SelectItem>
                <SelectItem value="always">Every session</SelectItem>
                <SelectItem value="never">Never</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {(() => {
                switch (leadPolicy) {
                  case "pii_required":
                    return `A ${t.opportunity.toLowerCase()} is created once the conversation captures an email or phone number (phone-identified channels such as WhatsApp qualify immediately).`
                  case "always":
                    return `Every session creates a ${t.opportunity.toLowerCase()} on the first AI reply — including a casual "hi" that goes nowhere, so expect name-only ${t.opportunityPlural.toLowerCase()} that need manual sorting.`
                  case "never":
                    return `Sessions never create ${t.opportunityPlural.toLowerCase()}; conversation notes and the CRM chat mirror stay off.`
                }
              })()}
            </p>
            {leadPolicy !== "never" && (
            <div className="flex items-start justify-between gap-4 border-t pt-4">
              <div>
                <Label htmlFor="sentiment-toggle" className="text-sm font-medium cursor-pointer">
                  Skip conversations the AI resolved
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isSentimentEnabled
                    ? `A conversation the AI closes successfully creates no ${t.opportunity.toLowerCase()} — only unresolved ones reach the CRM.`
                    : "Needs the Sentiment Analysis tool, enabled in the AI Reply tab."}
                </p>
              </div>
              <Switch
                id="sentiment-toggle"
                checked={bindingConfig.messaging?.require_sentiment || false}
                onCheckedChange={handleSentimentToggle}
                disabled={isSaving || !canManage || !isSentimentEnabled}
              />
            </div>
            )}

            <p className="text-xs text-muted-foreground">
              <span>Applies to ordinary conversations only. Escalations and AI-excluded contacts create
              their</span> {t.opportunityPlural.toLowerCase()} <span>whatever this is set to — each is switched
              on or off in</span> {t.opportunity.toLowerCase()} <span>routing above.</span>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Returning visitors */}
      {isCrmEnabled && selectedIntegration && (
        <Card>
          <CardHeader>
            <CardTitle>Returning Visitors</CardTitle>
            <CardDescription>
              <span>Control whether a new conversation session opens a new</span> {t.opportunity.toLowerCase()}<span>.</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50 border-blue-200">
              <div>
                <Label htmlFor="reuse-opportunity-toggle" className="text-sm font-medium text-gray-700 cursor-pointer">
                  <span>Keep writing into the open</span> {t.opportunity.toLowerCase()}
                </Label>
                <p className="text-xs text-gray-500 mt-1">
                  {providerReportsClosedState
                    ? `When a visitor returns, the new session continues the previous ${t.opportunity.toLowerCase()} for as long as it is open. A closed one still starts a new ${t.opportunity.toLowerCase()}. This lowers your ${t.opportunityPlural.toLowerCase()} count.`
                    : `${getProviderDisplayName(selectedIntegration.provider)} does not report whether a ${t.opportunity.toLowerCase()} is closed, so reuse cannot be offered safely.`}
                </p>
              </div>
              <Switch
                id="reuse-opportunity-toggle"
                checked={bindingConfig.messaging?.reuse_open_opportunity === true}
                onCheckedChange={handleReuseOpenOpportunityToggle}
                disabled={isSaving || !canManage || !providerReportsClosedState}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add CRM Dialog */}
      <AddCrmIntegrationDialog
        open={showAddCrmDialog}
        onOpenChange={setShowAddCrmDialog}
        providers={providers}
        onConnect={handleConnectCrm}
        onPrepareOAuth={prepareOAuthInstall}
        isConnecting={isConnecting}
        error={crmError}
        onClearError={clearCrmError}
      />
    </div>
  )
}
